import { useRef, useState } from "react";
import { X, Upload } from "lucide-react";
import { useDocuments } from "../modal/DocumentsContext";

interface Props {
  onClose: () => void;
  onImported: (documentId: number) => void;
}

/** Imports a .docx (e.g. downloaded from Google Docs) as a brand-new
 * document — always creates, never overwrites an existing one, mirroring
 * how Sheets' schema-creating CSV/XLSX import is admin-only. Formatting
 * (headings, bold/italic/underline, lists, links, images) round-trips on a
 * best-effort basis — see document_db.py's DOCX import/export section for
 * exactly what does and doesn't survive. */
export default function ImportDocxModal({ onClose, onImported }: Props) {
  const { importDocxCreate } = useDocuments();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(f: File | null) {
    setFile(f);
    if (f && !title.trim()) {
      setTitle(f.name.replace(/\.docx$/i, ""));
    }
  }

  async function handleImport() {
    if (!file) return;
    setImporting(true);
    setError(null);
    try {
      const document = await importDocxCreate(file, title.trim() || undefined);
      onImported(document.id);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Failed to import this file.");
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <Upload className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold text-foreground font-inter">Import Word Document</h2>
          </div>
          <button onClick={onClose} disabled={importing} className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-muted-foreground font-inter mb-4">
          Upload a .docx file — e.g. one downloaded from Google Docs (File → Download → Microsoft Word). This creates a new document; formatting is preserved on a best-effort basis.
        </p>

        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">File</label>
            <input
              ref={inputRef}
              type="file"
              accept=".docx"
              onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-foreground font-inter file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:bg-primary file:text-primary-foreground file:text-xs file:font-medium hover:file:bg-primary/90"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Defaults to the file name"
              className="w-full bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        {error && (
          <p className="text-xs text-destructive font-inter mb-4 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={importing}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-xl transition-colors disabled:opacity-40 font-inter"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={importing || !file}
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-inter"
          >
            {importing ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}
